import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  getCurrentStage,
  getCheckpoints,
  logActiveDay,
  completeBlock,
  type StageInfo,
  type Checkpoint,
} from '@/services/stages';
import { useTelegramBackButton } from '@/hooks/useTelegramBackButton';

const STAGE_NAMES: Record<number, string> = {
  1: 'КТО Я',
  2: 'МОЁ ТЕЛО',
  3: 'МОЙ ФОКУС',
  4: 'МОИ ЭМОЦИИ',
  5: 'МОИ СВЯЗИ',
  6: 'МОЯ СИЛА',
  7: 'МОЙ СМЫСЛ',
};

const STAGE_EMOJIS: Record<number, string> = {
  1: '🔮', 2: '💪', 3: '🎯', 4: '🌊', 5: '🤝', 6: '🚀', 7: '🧭',
};

const STAGE_DESCRIPTIONS: Record<number, { text: string; goal: string; duration: string }> = {
  1: {
    text: 'Фундамент пути. Ты исследуешь свои паттерны, ценности и то, как ты устроен.',
    goal: 'Понять свои сильные стороны и зоны роста через ежедневную практику осознанности.',
    duration: '21 день &middot; 3 блока по 7 дней',
  },
  2: {
    text: 'Работа с телом. Ты учишься слышать своё тело и управлять физической энергией.',
    goal: 'Выстроить устойчивую связь с телом и заложить привычки заботы о нём.',
    duration: '21 день &middot; 3 блока по 7 дней',
  },
  3: {
    text: 'Развитие внимания. Ты тренируешь фокус и учишься управлять вниманием.',
    goal: 'Научиться концентрироваться и замечать важное.',
    duration: '21 день &middot; 3 блока по 7 дней',
  },
  4: {
    text: 'Эмоциональный интеллект. Ты учишься распознавать и регулировать эмоции.',
    goal: 'Развить эмоциональную устойчивость и гибкость.',
    duration: '21 день &middot; 3 блока по 7 дней',
  },
  5: {
    text: 'Глубокие связи. Ты работаешь над отношениями и доверием.',
    goal: 'Укрепить связи с важными людьми и научиться быть уязвимым.',
    duration: '21 день &middot; 3 блока по 7 дней',
  },
  6: {
    text: 'Субъектность и сила. Ты берёшь ответственность и учишься действовать.',
    goal: 'Стать автором своей жизни и развить решительность.',
    duration: '21 день &middot; 3 блока по 7 дней',
  },
  7: {
    text: 'Смысл и интеграция. Ты находишь свою миссию и объединяешь все навыки.',
    goal: 'Обрести ясность цели и завершить первый цикл развития.',
    duration: '21 день &middot; 3 блока по 7 дней',
  },
};

interface StagesPageProps {
  onClose: () => void;
}

export function StagesPage({ onClose }: StagesPageProps) {
  const [stageInfo, setStageInfo] = useState<StageInfo | null>(null);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Telegram BackButton
  useTelegramBackButton(onClose);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [stage, cps] = await Promise.all([
        getCurrentStage(),
        getCheckpoints(),
      ]);
      setStageInfo(stage);
      setCheckpoints(cps);
    } catch (error) {
      console.error('Failed to load stages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogDay = async () => {
    setActionLoading(true);
    setMessage(null);
    try {
      const result = await logActiveDay();
      setMessage(result.message);
      await loadData();
    } catch (error: unknown) {
      setMessage((error as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteBlock = async () => {
    setActionLoading(true);
    setMessage(null);
    try {
      const result = await completeBlock();
      setMessage(result.message);
      await loadData();
    } catch (error: unknown) {
      setMessage((error as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-950 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 rounded-full border-3 border-primary-700 border-t-primary-400"
        />
      </div>
    );
  }

  if (!stageInfo) {
    return (
      <div className="min-h-screen bg-primary-950 flex flex-col items-center justify-center p-6">
        <p className="text-primary-400 mb-4">Не удалось загрузить этапы</p>
      </div>
    );
  }

  const overallProgress = Math.round((stageInfo.activeDays / stageInfo.totalDays) * 100);
  const bp = stageInfo.blockProgress;
  const canLogDay = stageInfo.hasTodayPractice && !stageInfo.alreadyLoggedToday;

  return (
    <div className="min-h-screen bg-primary-950 safe-area-top safe-area-bottom">
      {/* Header */}
      <header className="flex items-center justify-center p-4 border-b border-primary-800/50">
        <h1 className="text-white font-medium">Путь</h1>
      </header>

      <div className="overflow-y-auto p-4 space-y-4">
        {/* Current stage card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-gradient-to-br from-primary-800/60 to-primary-900/60 border border-primary-700/50 p-5"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="text-4xl">{STAGE_EMOJIS[stageInfo.stage] || '🔮'}</span>
            <div>
              <p className="text-xs text-primary-500 uppercase tracking-wide">Этап {stageInfo.stage} из 7</p>
              <h2 className="text-xl font-bold text-white">{stageInfo.stageName}</h2>
            </div>
          </div>

          {/* Stage description */}
          {STAGE_DESCRIPTIONS[stageInfo.stage] && (
            <div className="mb-4 p-3 rounded-lg bg-primary-900/50 border border-primary-700/30">
              <p className="text-sm text-primary-300 mb-2">{STAGE_DESCRIPTIONS[stageInfo.stage].text}</p>
              <p className="text-xs text-primary-400">
                <span className="font-medium text-primary-300">Цель:</span> {STAGE_DESCRIPTIONS[stageInfo.stage].goal}
              </p>
              <p
                className="text-xs text-primary-500 mt-1"
                dangerouslySetInnerHTML={{ __html: STAGE_DESCRIPTIONS[stageInfo.stage].duration }}
              />
            </div>
          )}

          {/* Overall progress */}
          <div className="mb-3">
            <div className="flex justify-between text-xs text-primary-400 mb-1">
              <span>Прогресс этапа</span>
              <span>{stageInfo.activeDays}/{stageInfo.totalDays} дней</span>
            </div>
            <div className="h-3 bg-primary-700/50 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 0.8 }}
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
              />
            </div>
          </div>

          {/* Pace mode */}
          <p className="text-xs text-primary-500">{stageInfo.paceLabel}</p>
        </motion.div>

        {/* Current block */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl bg-primary-800/30 border border-primary-700/50 p-4"
        >
          <h3 className="text-sm font-medium text-white mb-3">
            Блок {bp.block} из 3
          </h3>

          <div className="space-y-2">
            <ProgressRow
              label="Активные дни"
              current={bp.activeDays}
              target={7}
              emoji="📅"
            />
            <ProgressRow
              label="Трансферы (48ч)"
              current={bp.transfersCount}
              target={3}
              emoji="🔄"
            />
            <ProgressRow
              label="Реальные практики"
              current={bp.realPracticesCount}
              target={1}
              emoji="🎪"
            />
          </div>

          {/* Actions */}
          <div className="mt-4 space-y-2">
            <motion.button
              whileTap={{ scale: canLogDay ? 0.97 : 1 }}
              onClick={canLogDay ? handleLogDay : undefined}
              disabled={actionLoading || !canLogDay}
              className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-sm disabled:opacity-50"
            >
              {actionLoading ? '...' : stageInfo.alreadyLoggedToday ? 'День уже засчитан' : 'Засчитать день'}
            </motion.button>
            {!stageInfo.hasTodayPractice && !stageInfo.alreadyLoggedToday && (
              <p className="text-xs text-primary-500 text-center">Выполни вечернюю рефлексию, чтобы засчитать день</p>
            )}

            {bp.canComplete && (
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleCompleteBlock}
                disabled={actionLoading}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium text-sm disabled:opacity-50"
              >
                Завершить блок {bp.block}
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Message */}
        {message && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-xl bg-primary-800/50 border border-primary-700/50 p-3"
          >
            <p className="text-sm text-primary-300 text-center">{message}</p>
          </motion.div>
        )}

        {/* Stage roadmap */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl bg-primary-800/30 border border-primary-700/50 p-4"
        >
          <h3 className="text-sm font-medium text-white mb-3">Дорожная карта</h3>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6, 7].map(s => {
              const isCurrent = s === stageInfo.stage;
              const isCompleted = s < stageInfo.stage;
              const stageCheckpoints = checkpoints.filter(c => c.stage === s);
              const completedBlocks = stageCheckpoints.filter(c => c.status === 'completed').length;

              return (
                <div
                  key={s}
                  className={`flex items-center gap-3 p-2 rounded-lg ${
                    isCurrent ? 'bg-indigo-900/30 border border-indigo-800/30' : ''
                  }`}
                >
                  <span className="text-lg w-8 text-center">
                    {isCompleted ? '✅' : isCurrent ? STAGE_EMOJIS[s] : '🔒'}
                  </span>
                  <div className="flex-1">
                    <p className={`text-sm ${isCurrent ? 'text-white font-medium' : isCompleted ? 'text-primary-400' : 'text-primary-600'}`}>
                      {STAGE_NAMES[s]}
                    </p>
                    {(isCurrent || isCompleted) && (
                      <div className="flex gap-1 mt-1">
                        {[1, 2, 3].map(b => (
                          <div
                            key={b}
                            className={`h-1 flex-1 rounded-full ${
                              b <= completedBlocks
                                ? 'bg-green-500'
                                : isCurrent && b === stageInfo.currentBlock
                                ? 'bg-indigo-500'
                                : 'bg-primary-700/50'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {isCompleted && <span className="text-xs text-primary-500">3/3</span>}
                  {isCurrent && <span className="text-xs text-indigo-400">{completedBlocks}/3</span>}
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function ProgressRow({ label, current, target, emoji }: { label: string; current: number; target: number; emoji: string }) {
  const pct = Math.min(Math.round((current / target) * 100), 100);
  const met = current >= target;

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm">{emoji}</span>
      <div className="flex-1">
        <div className="flex justify-between text-xs mb-0.5">
          <span className="text-primary-400">{label}</span>
          <span className={met ? 'text-green-400' : 'text-primary-500'}>{current}/{target}</span>
        </div>
        <div className="h-1.5 bg-primary-700/50 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${met ? 'bg-green-500' : 'bg-indigo-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}
